import { z } from 'zod';
import { IObject } from '../IObject';
import { ReferralStatus, ReferralType } from '../enums';
import {
  dateValidation,
  emailValidation,
  nameValidation,
} from '../../validation';

// Fields that are used in all referrals (Default, Healix, L&G)
export const ReferralBaseSchema = z.object({
  type: z.enum(ReferralType),
  status: z.enum(ReferralStatus),
  source: z.string().optional(),

  // The case this referral is attached to
  caseId: z.string().optional(),

  cancelReason: z
    .object({
      key: z.string().min(1),
      value: z.string().min(1),
    })
    .optional(),
  // `consent` is declared by each organization's schema, not here: the create
  // and the stored shapes differ (see ReferralConsentSchema), and the fields an
  // organization spreads into this one would override whatever it said
  service: z
    .object({
      id: z.string().optional(),
      name: z.string().min(1),
      healee_ref: z.string().min(1),
      professional_type: z.string().min(1),
    })
    .optional(),

  pausedAt: dateValidation().optional(),
  // When the member created their Member Platform account through this
  // referral's sign-up link (PPL-4196). Kept once set, whatever the status
  // does next — some organizations bill on account creation.
  accountCreatedAt: dateValidation().optional(),
});

export type ReferralBaselData = z.infer<typeof ReferralBaseSchema>;
export type ReferralBase = IObject.HasIdTimestamped<ReferralBaselData>;

// A person EBP emails about the case - the insurer-side case manager, or the
// employer-side contact. Editable after the referral arrived because these
// change in the real world: a case manager leaves, an employer contact is
// only given later, an address is typed wrong on the form.
export const ReferralContactUpdateSchema = z.object({
  firstname: nameValidation('First name is required'),
  lastname: nameValidation('Last name is required'),
  email: emailValidation('Email is required'),
});
export type ReferralContactUpdate = z.infer<typeof ReferralContactUpdateSchema>;

// Every field is optional: PUT /referrals/:id is sent one contact at a time,
// and what it leaves out it leaves alone. A contact that IS sent must be
// complete - this corrects contacts, it never clears them.
export const ReferralUpdateSchema = z.object({
  caseManager: ReferralContactUpdateSchema.optional(),
  employer: z.object({ contact: ReferralContactUpdateSchema }).optional(),
});
export type ReferralUpdate = z.infer<typeof ReferralUpdateSchema>;
